import { createFileRoute, Link, useParams, useNavigate } from '@tanstack/react-router';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  Paper,
  Progress,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconActivity,
  IconArrowLeft,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandWhatsapp,
  IconBrandGoogle,
  IconChartBar,
  IconCheck,
  IconClock,
  IconEdit,
  IconMessage,
  IconClipboardText,
  IconNote,
  IconPlus,
  IconShoppingBag,
  IconTrash,
  IconUser,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { ContactForm, type ContactFormState } from '../../components/ContactForm';

function ContactDetail() {
  const { uid } = useParams({ from: '/vendor/contacts/$uid' });
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const contact = trpc.contacts.contacts.getByUid.useQuery({ uid });
  const contactId = contact.data?.id;

  const leadSub = trpc.integrations.google.getLeadSubmission.useQuery(
    { contactId: contactId ?? 0 },
    { enabled: !!contactId },
  );

  const timeline = trpc.contacts.timeline.list.useQuery(
    { contactId: contactId ?? 0, take: 100 },
    { enabled: !!contactId },
  );
  const summary = trpc.contacts.timeline.summary.useQuery(
    { contactId: contactId ?? 0 },
    { enabled: !!contactId },
  );
  const activity = trpc.contacts.timeline.activity.useQuery(
    { contactId: contactId ?? 0 },
    { enabled: !!contactId },
  );
  const notes = trpc.contacts.notes.list.useQuery(
    { contactId: contactId ?? 0 },
    { enabled: !!contactId },
  );

  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [editForm, setEditForm] = useState<ContactFormState | null>(null);

  const [conversionOpen, { open: openConversion, close: closeConversion }] = useDisclosure(false);
  const [conversionType, setConversionType] = useState<'Lead' | 'Purchase'>('Lead');
  const [conversionValue, setConversionValue] = useState<number | ''>('');
  const [conversionCurrency, setConversionCurrency] = useState('USD');

  const reportConversionMutation = trpc.integrations.google.reportManualConversion.useMutation({
    onSuccess: () => {
      notifications.show({
        message: 'Google Ads conversion reported successfully',
        color: 'green',
      });
      closeConversion();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const update = trpc.contacts.contacts.update.useMutation({
    onSuccess: () => {
      void utils.contacts.contacts.getByUid.invalidate({ uid });
      notifications.show({ message: 'Contact updated', color: 'green' });
      closeEdit();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const del = trpc.contacts.contacts.delete.useMutation({
    onSuccess: (r) => {
      const summary = r.ok ? r.value.summary : null;
      notifications.show({
        title: 'Contact deleted',
        message: summary
          ? `Wiped ${summary.messageLogs} messages, ${summary.notes} notes, ${summary.identities} identities, ${summary.customFieldValues} custom values.`
          : 'Done.',
        color: 'green',
      });
      navigate({ to: '/vendor/contacts' });
    },
  });

  const addNote = trpc.contacts.notes.create.useMutation({
    onSuccess: () => {
      void utils.contacts.notes.list.invalidate({ contactId });
      setNoteBody('');
      notifications.show({ message: 'Note added', color: 'green' });
    },
  });
  const deleteNote = trpc.contacts.notes.delete.useMutation({
    onSuccess: () => void utils.contacts.notes.list.invalidate({ contactId }),
  });
  const formsList = trpc.forms.list.useQuery();
  const sendForm = trpc.forms.send.useMutation({
    onSuccess: () => notifications.show({ message: 'Form sent', color: 'green' }),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [noteBody, setNoteBody] = useState('');

  if (contact.isPending) return <Skeleton height={500} />;
  if (contact.isError || !contact.data) {
    return (
      <Stack>
        <Text c="red">Contact not found</Text>
        <Button component={Link} to="/vendor/contacts" leftSection={<IconArrowLeft size={14} />}>
          Back to contacts
        </Button>
      </Stack>
    );
  }
  const c = contact.data;

  const startEdit = () => {
    setEditForm({
      firstName: c.firstName ?? '',
      lastName: c.lastName ?? '',
      waId: c.waId ?? '',
      email: c.email ?? '',
      languageCode: c.languageCode ?? '',
      countryId: c.countryId,
      whatsappOptOut: c.isOptedOut,
      enableAiBot: !c.isAiBotDisabled,
      enableReplyBot: !c.isReplyBotDisabled,
      groupIds: c.groupIds,
      customFieldValues: c.customFieldValues,
    });
    openEdit();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Modal
          opened={conversionOpen}
          onClose={closeConversion}
          title="Record Google Ads Conversion"
          size="sm"
        >
          <Stack gap="md">
            <Text size="xs" c="dimmed">
              Manually report an offline conversion to Google Ads for this contact. This will be
              queued in the outbox and uploaded using the contact's Google Click ID (GCLID).
            </Text>

            <Select
              label="Conversion Type"
              data={[
                { value: 'Lead', label: 'Lead' },
                { value: 'Purchase', label: 'Purchase' },
              ]}
              value={conversionType}
              onChange={(val) => setConversionType(val as 'Lead' | 'Purchase')}
            />

            <NumberInput
              label="Value (Optional)"
              placeholder="e.g. 150.00"
              value={conversionValue === '' ? undefined : conversionValue}
              onChange={(val) => setConversionValue(typeof val === 'number' ? val : '')}
              min={0}
              decimalScale={2}
            />

            <TextInput
              label="Currency"
              value={conversionCurrency}
              onChange={(e) => setConversionCurrency(e.target.value)}
              placeholder="USD"
            />

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeConversion}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={() =>
                  reportConversionMutation.mutate({
                    contactId: c.id,
                    eventName: conversionType,
                    value: conversionValue === '' ? undefined : conversionValue,
                    currency: conversionCurrency || undefined,
                  })
                }
                loading={reportConversionMutation.isPending}
              >
                Upload
              </Button>
            </Group>
          </Stack>
        </Modal>
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            component={Link}
            to="/vendor/contacts"
          >
            Contacts
          </Button>
        </Group>

        <Card>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group wrap="nowrap" gap="md">
              <Avatar size="lg" radius="xl">
                {c.firstName?.[0] ?? c.email?.[0] ?? '?'}
              </Avatar>
              <Box>
                <Title order={3}>{c.fullName || '(unnamed contact)'}</Title>
                <Group gap="xs" mt={4}>
                  {c.channels.includes('whatsapp') && (
                    <Tooltip label={c.waIdDisplay ?? c.waId ?? ''}>
                      <Badge
                        leftSection={<IconBrandWhatsapp size={12} />}
                        color="green"
                        variant="light"
                      >
                        WhatsApp
                      </Badge>
                    </Tooltip>
                  )}
                  {c.channels.includes('messenger') && (
                    <Badge
                      leftSection={<IconBrandFacebook size={12} />}
                      color="blue"
                      variant="light"
                    >
                      Messenger
                    </Badge>
                  )}
                  {c.channels.includes('instagram') && (
                    <Badge
                      leftSection={<IconBrandInstagram size={12} />}
                      color="pink"
                      variant="light"
                    >
                      Instagram {c.instagramUsername ? `@${c.instagramUsername}` : ''}
                    </Badge>
                  )}
                  {c.email && (
                    <Badge variant="light" color="gray">
                      {c.email}
                    </Badge>
                  )}
                </Group>
                {/* One-click jump to the linked conversation in each channel's
                    inbox (deep-links via ?contactId). */}
                <Group mt={8} gap="xs">
                  {c.channels.includes('whatsapp') && (
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      leftSection={<IconBrandWhatsapp size={14} />}
                      renderRoot={(props) => (
                        <Link to="/vendor/chat" search={{ contactId: c.id }} {...props} />
                      )}
                    >
                      Open WhatsApp
                    </Button>
                  )}
                  {c.channels.includes('messenger') && (
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      leftSection={<IconBrandFacebook size={14} />}
                      renderRoot={(props) => (
                        <Link to="/vendor/facebook/inbox" search={{ contactId: c.id }} {...props} />
                      )}
                    >
                      Open Messenger
                    </Button>
                  )}
                  {c.channels.includes('instagram') && (
                    <Button
                      size="xs"
                      variant="light"
                      color="pink"
                      leftSection={<IconBrandInstagram size={14} />}
                      renderRoot={(props) => (
                        <Link
                          to="/vendor/instagram/inbox"
                          search={{ contactId: c.id }}
                          {...props}
                        />
                      )}
                    >
                      Open Instagram
                    </Button>
                  )}
                </Group>
                <Group mt={6} gap="xs">
                  <Tooltip label={`Engagement score ${c.engagementScore}/100`}>
                    <Box w={120}>
                      <Progress
                        value={c.engagementScore}
                        size="sm"
                        color={
                          c.engagementScore >= 70
                            ? 'green'
                            : c.engagementScore >= 40
                              ? 'yellow'
                              : 'gray'
                        }
                      />
                    </Box>
                  </Tooltip>
                  <Text size="xs" c="dimmed">
                    score {c.engagementScore}
                  </Text>
                  {c.isOptedOut && (
                    <Badge color="orange" variant="light" size="xs">
                      opted out
                    </Badge>
                  )}
                  {c.isUnreachable && (
                    <Badge color="red" variant="light" size="xs">
                      unreachable (Meta {c.lastSendErrorCode})
                    </Badge>
                  )}
                  {c.isBlocked && (
                    <Badge color="red" variant="light" size="xs">
                      blocked
                    </Badge>
                  )}
                </Group>
              </Box>
            </Group>
            <Group>
              {c.channels.includes('whatsapp') && c.waId && (
                <Menu position="bottom-end" withArrow>
                  <Menu.Target>
                    <Button variant="default" leftSection={<IconClipboardText size={14} />}>
                      Send form
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {formsList.isPending ? (
                      <Menu.Item disabled>
                        <Loader size="xs" />
                      </Menu.Item>
                    ) : (
                      (() => {
                        const published = (formsList.data ?? []).filter(
                          (f) => f.status === 'PUBLISHED',
                        );
                        if (published.length === 0) {
                          return (
                            <Menu.Item disabled>No published forms — publish one first</Menu.Item>
                          );
                        }
                        return published.map((f) => (
                          <Menu.Item
                            key={f.uid}
                            onClick={() =>
                              sendForm.mutate({ uid: f.uid, to: c.waId!, contactId: c.id })
                            }
                          >
                            📋 {f.name}
                          </Menu.Item>
                        ));
                      })()
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
              <Button
                variant="default"
                leftSection={<IconBrandGoogle size={14} />}
                onClick={openConversion}
              >
                Record Conversion
              </Button>
              <Button variant="default" leftSection={<IconEdit size={14} />} onClick={startEdit}>
                Edit
              </Button>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete ${c.fullName || c.email || c.waId}? This cascades to every message log, note, group link, label link, custom-field value, and channel identity for this contact. The deletion is logged for App-Review compliance.`,
                    )
                  ) {
                    del.mutate({ contactId: c.id });
                  }
                }}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Card>

        <Tabs defaultValue="overview">
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconUser size={14} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="conversation" leftSection={<IconMessage size={14} />}>
              Conversation
            </Tabs.Tab>
            <Tabs.Tab value="notes" leftSection={<IconNote size={14} />}>
              Notes ({notes.data?.length ?? 0})
            </Tabs.Tab>
            <Tabs.Tab value="activity" leftSection={<IconActivity size={14} />}>
              Activity
            </Tabs.Tab>
            <Tabs.Tab value="engagement" leftSection={<IconChartBar size={14} />}>
              Engagement
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Stack gap="md">
              <Card>
                <Stack gap="xs">
                  <KV label="WhatsApp" value={c.waIdDisplay ?? '—'} />
                  <KV label="Messenger PSID" value={c.facebookPageScopedId ?? '—'} />
                  <KV label="Instagram IGSID" value={c.instagramScopedId ?? '—'} />
                  <KV
                    label="Instagram username"
                    value={c.instagramUsername ? `@${c.instagramUsername}` : '—'}
                  />
                  <KV label="Email" value={c.email ?? '—'} />
                  <KV label="Language" value={c.languageCode ?? '—'} />
                  <KV label="Country id" value={c.countryId ? String(c.countryId) : '—'} />
                  <KV label="AI bot" value={c.isAiBotDisabled ? 'disabled' : 'enabled'} />
                  <KV label="Reply bot" value={c.isReplyBotDisabled ? 'disabled' : 'enabled'} />
                  <KV label="Created" value={new Date(c.createdAt).toLocaleString()} />
                  {c.labels.length > 0 && (
                    <Group gap={4} mt="xs">
                      {c.labels.map((l) => (
                        <Badge
                          key={l.id}
                          size="sm"
                          variant="light"
                          style={{
                            backgroundColor: l.bgColor ?? undefined,
                            color: l.textColor ?? undefined,
                          }}
                        >
                          {l.title}
                        </Badge>
                      ))}
                    </Group>
                  )}
                  {c.groups.length > 0 && (
                    <Group gap={4} mt="xs">
                      {c.groups.map((g) => (
                        <Badge key={g} size="sm" variant="light" color="cyan">
                          {g}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </Stack>
              </Card>

              {leadSub.data && (
                <Card withBorder padding="md" radius="sm">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text fw={600} size="sm">
                        Google Lead Details
                      </Text>
                      <Badge
                        color="red"
                        variant="light"
                        leftSection={<IconBrandGoogle size={12} />}
                      >
                        {leadSub.data.leadSource === 'LOCAL_SERVICES'
                          ? 'Local Services Ads'
                          : 'Google Ads'}
                      </Badge>
                    </Group>
                    <Divider />
                    <KV label="Lead ID" value={leadSub.data.leadId} />
                    {leadSub.data.gclId && <KV label="GCLID" value={leadSub.data.gclId} />}
                    <KV
                      label="Received At"
                      value={new Date(leadSub.data.receivedAt).toLocaleString()}
                    />

                    {leadSub.data.leadSource === 'LOCAL_SERVICES' && leadSub.data.fieldData && (
                      <>
                        <KV label="Category" value={leadSub.data.fieldData.category || '—'} />
                        <KV label="Service" value={leadSub.data.fieldData.service || '—'} />
                        <KV label="Lead Status" value={leadSub.data.fieldData.status || '—'} />
                        <KV label="Lead Type" value={leadSub.data.fieldData.leadType || '—'} />

                        {leadSub.data.fieldData.leadType === 'PHONE_CALL' &&
                          leadSub.data.fieldData.callDetails && (
                            <Card withBorder padding="xs" radius="xs" mt="xs" bg="gray.0">
                              <Stack gap="xs">
                                <Text size="xs" fw={600}>
                                  Call Recording
                                </Text>
                                {leadSub.data.fieldData.callDetails.callDurationMillis && (
                                  <Text size="xs" c="dimmed">
                                    Duration:{' '}
                                    {(() => {
                                      const ms =
                                        leadSub.data.fieldData.callDetails.callDurationMillis;
                                      const totalSecs = Math.floor(ms / 1000);
                                      const mins = Math.floor(totalSecs / 60);
                                      const secs = totalSecs % 60;
                                      return `${mins}:${secs.toString().padStart(2, '0')}`;
                                    })()}
                                  </Text>
                                )}
                                {leadSub.data.fieldData.callDetails.leadActionPlayAudioUrl && (
                                  <audio
                                    controls
                                    src={leadSub.data.fieldData.callDetails.leadActionPlayAudioUrl}
                                    style={{ width: '100%', height: '40px' }}
                                  />
                                )}
                              </Stack>
                            </Card>
                          )}
                      </>
                    )}
                  </Stack>
                </Card>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="conversation" pt="md">
            <Card p={0}>
              <ScrollArea h={500}>
                {timeline.isPending ? (
                  <Skeleton height={400} m="md" />
                ) : (timeline.data?.items ?? []).length === 0 ? (
                  <Text c="dimmed" p="md" ta="center">
                    No messages yet across any channel.
                  </Text>
                ) : (
                  <Stack gap={6} p="sm">
                    {(timeline.data?.items ?? []).map((m) => (
                      <Paper
                        key={`${m.channel}-${m.id}`}
                        p="xs"
                        radius="md"
                        style={{
                          alignSelf: m.isIncomingMessage === 1 ? 'flex-start' : 'flex-end',
                          maxWidth: '70%',
                          // Theme-aware bubble background so the (adaptive) message text stays
                          // legible in dark mode. The old `bg="gray.1"`/`green.1` shades were light
                          // in BOTH color schemes, which hid the light dark-mode text.
                          backgroundColor:
                            m.isIncomingMessage === 1
                              ? 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))'
                              : m.channel === 'whatsapp'
                                ? 'light-dark(var(--mantine-color-green-1), var(--mantine-color-green-9))'
                                : m.channel === 'messenger'
                                  ? 'light-dark(var(--mantine-color-blue-1), var(--mantine-color-blue-9))'
                                  : 'light-dark(var(--mantine-color-pink-1), var(--mantine-color-pink-9))',
                          color:
                            'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))',
                        }}
                      >
                        <Group gap={4} mb={2}>
                          <Badge size="xs" variant="dot">
                            {m.channel}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {m.messagedAt ? new Date(m.messagedAt).toLocaleString() : ''}
                          </Text>
                        </Group>
                        <Text size="sm">{m.message ?? `[${m.messageType ?? 'event'}]`}</Text>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </ScrollArea>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="notes" pt="md">
            <Stack gap="md">
              <Card>
                <Stack gap="xs">
                  <Textarea
                    placeholder="Add a note about this contact…"
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.currentTarget.value)}
                    autosize
                    minRows={2}
                    maxRows={6}
                  />
                  <Group justify="end">
                    <Button
                      size="xs"
                      leftSection={<IconPlus size={12} />}
                      disabled={noteBody.trim().length === 0}
                      loading={addNote.isPending}
                      onClick={() => addNote.mutate({ contactId: c.id, body: noteBody.trim() })}
                    >
                      Add note
                    </Button>
                  </Group>
                </Stack>
              </Card>
              {notes.isPending ? (
                <Skeleton height={200} />
              ) : (
                (notes.data ?? []).map((n) => (
                  <Card key={n.id}>
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed">
                        {new Date(n.createdAt).toLocaleString()}
                      </Text>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => deleteNote.mutate({ noteId: n.id })}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                    <Text mt="xs" style={{ whiteSpace: 'pre-wrap' }}>
                      {n.body}
                    </Text>
                  </Card>
                ))
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="activity" pt="md">
            <Card p={0}>
              <Stack gap={0}>
                {activity.isPending ? (
                  <Skeleton height={200} m="md" />
                ) : (activity.data ?? []).length === 0 ? (
                  <Text c="dimmed" p="md" ta="center">
                    No activity recorded yet.
                  </Text>
                ) : (
                  (activity.data ?? []).map((a) => (
                    <Group key={a.id} p="sm" justify="space-between">
                      <Group gap="xs">
                        <IconClock size={14} />
                        <Text size="sm" fw={500}>
                          {a.action}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {new Date(a.timestamp).toLocaleString()}
                      </Text>
                    </Group>
                  ))
                )}
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="engagement" pt="md">
            <Stack gap="md">
              <Group grow>
                <Card>
                  <Text c="dimmed" size="xs">
                    Inbound 30d
                  </Text>
                  <Title order={3}>{summary.data?.inbound30d ?? 0}</Title>
                </Card>
                <Card>
                  <Text c="dimmed" size="xs">
                    Outbound 30d
                  </Text>
                  <Title order={3}>{summary.data?.outbound30d ?? 0}</Title>
                </Card>
                <Card>
                  <Text c="dimmed" size="xs">
                    Response rate
                  </Text>
                  <Title order={3}>{Math.round((summary.data?.responseRate30d ?? 0) * 100)}%</Title>
                </Card>
              </Group>

              {(summary.data?.productInterests ?? []).length > 0 && (
                <Card>
                  <Group mb="xs">
                    <IconShoppingBag size={16} />
                    <Text fw={600} size="sm">
                      Product interest
                    </Text>
                  </Group>
                  <Stack gap={4}>
                    {(summary.data?.productInterests ?? []).map((p) => (
                      <Group key={p.id} justify="space-between">
                        <Text size="sm" ff="monospace">
                          {p.productRetailerId}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(p.capturedAt).toLocaleDateString()}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              )}

              {(summary.data?.recentCampaigns ?? []).length > 0 && (
                <Card>
                  <Text fw={600} size="sm" mb="xs">
                    Recent campaigns received
                  </Text>
                  <Stack gap={4}>
                    {(summary.data?.recentCampaigns ?? []).map((c, i) => (
                      <Group key={i} justify="space-between">
                        <Text size="sm">Campaign #{c.campaignId}</Text>
                        <Group gap={6}>
                          <Badge variant="light" size="xs">
                            {c.status ?? '—'}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {c.messagedAt ? new Date(c.messagedAt).toLocaleDateString() : ''}
                          </Text>
                        </Group>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Edit modal */}
      <Modal opened={editOpen} onClose={closeEdit} title={`Edit ${c.fullName}`} size="lg" centered>
        {editForm && (
          <Stack>
            <ContactForm state={editForm} onChange={setEditForm} />
            <Group justify="end">
              <Button variant="default" onClick={closeEdit}>
                Cancel
              </Button>
              <Button
                loading={update.isPending}
                leftSection={<IconCheck size={14} />}
                onClick={() =>
                  update.mutate({
                    contactId: c.id,
                    firstName: editForm.firstName || null,
                    lastName: editForm.lastName || null,
                    waId: editForm.waId || null,
                    email: editForm.email || null,
                    languageCode: editForm.languageCode || null,
                    countryId: editForm.countryId,
                    whatsappOptOut: editForm.whatsappOptOut,
                    enableAiBot: editForm.enableAiBot,
                    enableReplyBot: editForm.enableReplyBot,
                    groupIds: editForm.groupIds,
                    customFieldValues: editForm.customFieldValues,
                  })
                }
              >
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </motion.div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text c="dimmed" size="sm">
        {label}
      </Text>
      <Text size="sm" ff={value.length > 20 ? 'monospace' : undefined}>
        {value}
      </Text>
    </Group>
  );
}

export const Route = createFileRoute('/vendor/contacts/$uid')({ component: ContactDetail });
